import type { TagResponse } from "@redotech/effect-api-shared/rpc/tag";
import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import {
  BadgePillThemeToColorMapping,
  BadgePillThemeToTypeMapping,
} from "@redotech/redo-web/tags/tag-pill";

export function createSupportTicketTagsAdvancedFilter(
  tags: TagResponse[],
): ArrayStringTableFilter<TagResponse | string> {
  function resolveTag(item: TagResponse | string): {
    id: string;
    name: string;
    pillTheme: PillTheme;
  } {
    if (typeof item !== "string") {
      return {
        id: item.id,
        name: item.name,
        pillTheme: item.pillTheme ?? PillTheme.NORMAL,
      };
    }
    const found = tags.find((t) => t.id === item);
    return {
      id: item,
      name: found?.name ?? item,
      pillTheme: found?.pillTheme ?? PillTheme.NORMAL,
    };
  }

  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: TagIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.SUPPORT_TICKET_TAGS],
    itemLabel: "tag",
    itemLabelPlural: "tags",
    displayName: "Support ticket tags",
    dbSearch: false,
    values: tags,
    itemGenerator: (item) => {
      const tag = resolveTag(item);
      return {
        id: tag.id,
        value: item,
        type: "badge" as const,
        badge: {
          text: tag.name,
          color: BadgePillThemeToColorMapping[tag.pillTheme],
          type: BadgePillThemeToTypeMapping[tag.pillTheme],
        },
      };
    },
    stringify: (item) => (typeof item === "string" ? item : item.id),
    display: (item) => resolveTag(item).name,
  };
}
